import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  CurrentsAPI,
  type OceanAgent,
  type OceanAgentRole,
  type OceanAgentVisibility,
  type OceanBedrockModel,
  type OceanOrgGuardrail,
  type OceanOrgMemory,
  type OceanOrgSkill,
  type OrchestratorConfig,
  type OrchestratorPeerConfig,
} from "@vida/currents-core";
import { currentUserSub } from "../auth";
import { AgentAvatar } from "../components/AgentAvatar";
import {
  AgentEditorSidebar,
  type EditorSection,
} from "../components/agent-editor/AgentEditorSidebar";
import { AgentIconUpload } from "../components/agent-editor/AgentIconUpload";
import { loadBedrockModels } from "../services/bedrockModels";
import { buildAgentInferenceConfigJson } from "../utils/bedrockInferenceConfig";
import {
  createCurrentsApiConfig,
  ensureOceanOrgBootstrapped,
  personalOrgId,
} from "../services/currentsSession";
import {
  AGENT_ROLES,
  DEFAULT_MODEL_ID,
  DEFAULT_SYSTEM_PROMPT,
  isKnownDefaultSystemPrompt,
  OCEAN_ASSIGNABLE_APP_IDS,
  PICKABLE_ICONS,
  defaultPermissionsJson,
  iconForRole,
  RETRIEVAL_AGENT_TEMPLATE,
  ILLUSTRATOR_AGENT_TEMPLATE,
  roleLabel,
  systemPromptForRole,
  toolsJsonFromSkillIds,
} from "../services/agentCatalog";

const SECTIONS: EditorSection[] = ["profile", "assignments", "access", "team", "budget"];

function parseSection(raw: string | null): EditorSection {
  if (raw && SECTIONS.includes(raw as EditorSection)) {
    return raw as EditorSection;
  }
  return "profile";
}

function parseOrchestratorConfig(raw?: string | null): OrchestratorConfig {
  if (!raw) return { peers: [] };
  try {
    const parsed = JSON.parse(raw) as OrchestratorConfig;
    return { peers: Array.isArray(parsed.peers) ? parsed.peers : [] };
  } catch {
    return { peers: [] };
  }
}

export function AgentEditorPage() {
  const { agentId } = useParams();
  const isNew = !agentId;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const section = parseSection(searchParams.get("section"));
  const cfg = createCurrentsApiConfig();

  const [orgId, setOrgId] = useState(() => personalOrgId(currentUserSub()));
  const [name, setName] = useState("");
  const [role, setRole] = useState<OceanAgentRole>("RESEARCHER");
  const [icon, setIcon] = useState(iconForRole("RESEARCHER"));
  const [iconS3Key, setIconS3Key] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [systemPrompt, setSystemPrompt] = useState(DEFAULT_SYSTEM_PROMPT);
  const [modelId, setModelId] = useState(DEFAULT_MODEL_ID);
  const [skillIds, setSkillIds] = useState<string[]>(["semantic-search", "web-research"]);
  const [memoryIds, setMemoryIds] = useState<string[]>(["session-mission"]);
  const [guardrailIds, setGuardrailIds] = useState<string[]>(["read-only", "cite-sources"]);
  const [orchestratorPeers, setOrchestratorPeers] = useState<OrchestratorPeerConfig[]>([]);
  const [maxCostCents, setMaxCostCents] = useState(200);
  const [maxToolCalls, setMaxToolCalls] = useState(25);
  const [enabled, setEnabled] = useState(true);
  const [appIds, setAppIds] = useState<string[]>(["currents"]);
  const [visibility, setVisibility] = useState<OceanAgentVisibility>("PRIVATE");
  const [minAccessLevel, setMinAccessLevel] = useState(0);

  const [orgSkills, setOrgSkills] = useState<OceanOrgSkill[]>([]);
  const [orgMemories, setOrgMemories] = useState<OceanOrgMemory[]>([]);
  const [orgGuardrails, setOrgGuardrails] = useState<OceanOrgGuardrail[]>([]);
  const [bedrockModels, setBedrockModels] = useState<OceanBedrockModel[]>([]);
  const [bedrockModelsError, setBedrockModelsError] = useState<string | null>(null);
  const [allAgents, setAllAgents] = useState<OceanAgent[]>([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const peerCandidates = useMemo(
    () => allAgents.filter((a) => a.id !== agentId && a.enabled),
    [allAgents, agentId],
  );

  useEffect(() => {
    if (section === "team" && role !== "ORCHESTRATOR") {
      setSearchParams({ section: "profile" }, { replace: true });
    }
  }, [section, role, setSearchParams]);

  // Catalogo modelli: fetch admin-api indipendente da GraphQL (skills/agenti).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const modelLoad = await loadBedrockModels();
      if (cancelled) return;
      setBedrockModels(modelLoad.models);
      setBedrockModelsError(
        modelLoad.source === "fallback" || modelLoad.warnings.length > 0
          ? modelLoad.warnings.join(" · ")
          : null,
      );
      if (
        isNew &&
        modelLoad.models.length > 0 &&
        !modelLoad.models.some((m) => m.modelId === DEFAULT_MODEL_ID)
      ) {
        setModelId(modelLoad.models[0]!.modelId);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNew]);

  useEffect(() => {
    void (async () => {
      try {
        await ensureOceanOrgBootstrapped(cfg);
        const [agents, skills, memories, guardrails] = await Promise.all([
          CurrentsAPI.listAgents(cfg),
          CurrentsAPI.listOrgSkills(cfg),
          CurrentsAPI.listOrgMemories(cfg),
          CurrentsAPI.listOrgGuardrails(cfg),
        ]);
        setAllAgents(agents);
        setOrgSkills(skills);
        setOrgMemories(memories);
        setOrgGuardrails(guardrails);

        if (!isNew && agentId) {
          const agent = await CurrentsAPI.getAgent(cfg, agentId);
          if (!agent) {
            setError("Agente non trovato");
            return;
          }
          applyAgent(agent);
        } else if (isNew && searchParams.get("template") === "illustrator") {
          const t = ILLUSTRATOR_AGENT_TEMPLATE;
          setName(t.name);
          setRole(t.role);
          setIcon(t.icon);
          setDescription(t.description);
          setSystemPrompt(t.systemPromptTemplate);
          setSkillIds(t.skillIds);
          setGuardrailIds(t.guardrailIds);
          try {
            const perms = JSON.parse(t.permissionsJson) as {
              budget?: { maxCostCents?: number; maxToolCalls?: number };
            };
            if (perms.budget?.maxCostCents) setMaxCostCents(perms.budget.maxCostCents);
            if (perms.budget?.maxToolCalls) setMaxToolCalls(perms.budget.maxToolCalls);
          } catch {
            /* ignore */
          }
        } else if (isNew && searchParams.get("template") === "retrieval") {
          const t = RETRIEVAL_AGENT_TEMPLATE;
          setName(t.name);
          setRole(t.role);
          setIcon(t.icon);
          setDescription(t.description);
          setSystemPrompt(t.systemPromptTemplate);
          setSkillIds(t.skillIds);
          setGuardrailIds(t.guardrailIds);
          try {
            const perms = JSON.parse(t.permissionsJson) as {
              budget?: { maxCostCents?: number; maxToolCalls?: number };
            };
            if (perms.budget?.maxCostCents) setMaxCostCents(perms.budget.maxCostCents);
            if (perms.budget?.maxToolCalls) setMaxToolCalls(perms.budget.maxToolCalls);
          } catch {
            /* ignore */
          }
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [agentId, isNew]);

  function applyAgent(agent: OceanAgent) {
    setOrgId(agent.orgId || personalOrgId(currentUserSub()));
    setName(agent.name);
    setRole(agent.role);
    setIcon(agent.icon || iconForRole(agent.role));
    setIconS3Key(agent.iconS3Key ?? null);
    setDescription(agent.description ?? "");
    setSystemPrompt(agent.systemPromptTemplate);
    setModelId(agent.modelId);
    setSkillIds(agent.skillIds?.length ? agent.skillIds : agent.skills ?? []);
    setMemoryIds(agent.memoryIds ?? []);
    setGuardrailIds(agent.guardrailIds ?? []);
    setOrchestratorPeers(parseOrchestratorConfig(agent.orchestratorConfigJson).peers);
    setEnabled(agent.enabled);
    setAppIds(agent.appIds?.length ? agent.appIds : ["currents"]);
    setVisibility(agent.visibility ?? "PRIVATE");
    setMinAccessLevel(
      typeof agent.minAccessLevel === "number" && agent.minAccessLevel >= 0
        ? Math.min(2, agent.minAccessLevel)
        : 0,
    );
    try {
      const perms = JSON.parse(agent.permissionsJson || "{}") as {
        budget?: { maxCostCents?: number; maxToolCalls?: number };
      };
      if (perms.budget?.maxCostCents) setMaxCostCents(perms.budget.maxCostCents);
      if (perms.budget?.maxToolCalls) setMaxToolCalls(perms.budget.maxToolCalls);
    } catch {
      /* defaults */
    }
  }

  function onSectionChange(next: EditorSection) {
    setSearchParams({ section: next });
  }

  function onRoleChange(next: OceanAgentRole) {
    if (next === role) return;

    const nextDefaultPrompt = systemPromptForRole(next);

    if (!isNew) {
      const replace = window.confirm(
        `Vuoi sostituire il system prompt con quello predefinito per il ruolo «${roleLabel(next)}»?\n\n` +
          "Se annulli, cambia solo il ruolo e il prompt attuale resta invariato.",
      );
      if (replace) {
        setSystemPrompt(nextDefaultPrompt);
      }
    } else if (isKnownDefaultSystemPrompt(systemPrompt)) {
      setSystemPrompt(nextDefaultPrompt);
    }

    setRole(next);
    if (!iconS3Key && (!icon || !PICKABLE_ICONS.includes(icon))) {
      setIcon(iconForRole(next));
    }
    if (next !== "ORCHESTRATOR" && section === "team") {
      onSectionChange("profile");
    }
  }

  function toggleId(list: string[], id: string, setter: (v: string[]) => void) {
    setter(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  }

  function peerConfig(agentIdPeer: string): OrchestratorPeerConfig {
    return (
      orchestratorPeers.find((p) => p.agentId === agentIdPeer) ?? {
        agentId: agentIdPeer,
        shareIdentity: true,
        shareSkills: true,
      }
    );
  }

  function setPeerSelected(agentIdPeer: string, selected: boolean) {
    if (!selected) {
      setOrchestratorPeers((prev) => prev.filter((p) => p.agentId !== agentIdPeer));
      return;
    }
    if (orchestratorPeers.some((p) => p.agentId === agentIdPeer)) return;
    setOrchestratorPeers((prev) => [
      ...prev,
      { agentId: agentIdPeer, shareIdentity: true, shareSkills: true },
    ]);
  }

  function updatePeer(agentIdPeer: string, patch: Partial<OrchestratorPeerConfig>) {
    setOrchestratorPeers((prev) =>
      prev.map((p) => (p.agentId === agentIdPeer ? { ...p, ...patch } : p)),
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);

    const knowledgeWrite = skillIds.includes("web-ingest");
    const permissionsJson = JSON.stringify({
      dataScopes: [
        { domain: "notes", actions: ["read"] },
        ...(knowledgeWrite ? [] : [{ domain: "reef", actions: ["read"] }]),
        {
          domain: "knowledge",
          actions: knowledgeWrite ? ["read", "write"] : ["read"],
        },
      ],
      layers: ["surface", "epipelagic", "mesopelagic"],
      outbound: ["web"],
      budget: { maxCostCents, maxToolCalls },
    });

    const orchestratorConfigJson =
      role === "ORCHESTRATOR"
        ? JSON.stringify({ peers: orchestratorPeers })
        : undefined;

    const payload = {
      name: name.trim(),
      role,
      icon,
      iconS3Key: iconS3Key || null,
      description: description.trim() || undefined,
      systemPromptTemplate: systemPrompt,
      modelId: modelId.trim(),
      inferenceConfigJson: buildAgentInferenceConfigJson(modelId.trim()),
      skillIds,
      skills: skillIds,
      memoryIds,
      guardrailIds,
      toolsJson: toolsJsonFromSkillIds(skillIds, orgSkills),
      permissionsJson: permissionsJson || defaultPermissionsJson(),
      orchestratorConfigJson,
      visibility,
      minAccessLevel,
      appIds: appIds.length > 0 ? appIds : ["currents"],
      enabled,
    };

    try {
      if (isNew) {
        const created = await CurrentsAPI.createAgent(cfg, payload);
        navigate(`/agents/${created.id}/edit?section=${section}`, { replace: true });
      } else if (agentId) {
        await CurrentsAPI.updateAgent(cfg, agentId, payload);
        navigate("/agents");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="page">
        <p className="muted">Caricamento…</p>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header">
        <h1>{isNew ? "Nuovo agente" : "Modifica agente"}</h1>
        <Link className="btn secondary" to="/agents">
          Indietro
        </Link>
      </header>

      <div className="editor-layout">
        <AgentEditorSidebar section={section} role={role} onSectionChange={onSectionChange} />

        <form className="card editor-main" onSubmit={(e) => void onSubmit(e)}>
          {error && <p className="err">{error}</p>}

          {section === "profile" && (
            <>
              <h2 className="editor-panel-title">Profilo</h2>
              <div className="grid-2">
                <div>
                  <label htmlFor="name">Nome</label>
                  <input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
                <div>
                  <label htmlFor="role">Ruolo</label>
                  <select
                    id="role"
                    value={role}
                    onChange={(e) => onRoleChange(e.target.value as OceanAgentRole)}
                  >
                    {AGENT_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {roleLabel(r)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ marginTop: "1rem" }}>
                <label>Icona agente</label>
                <AgentIconUpload
                  orgId={orgId}
                  agentId={agentId}
                  role={role}
                  emoji={icon}
                  iconS3Key={iconS3Key}
                  onEmojiChange={setIcon}
                  onIconS3KeyChange={setIconS3Key}
                />
              </div>

              <div style={{ marginTop: "1rem" }}>
                <label htmlFor="description">Descrizione</label>
                <input
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div style={{ marginTop: "1rem" }}>
                <label htmlFor="prompt">System prompt</label>
                <textarea
                  id="prompt"
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  required
                />
              </div>

              <div className="grid-2" style={{ marginTop: "1rem" }}>
                <div>
                  <label htmlFor="model">Modello Bedrock</label>
                  <select
                    id="model"
                    value={modelId}
                    onChange={(e) => setModelId(e.target.value)}
                    required
                  >
                    {!bedrockModels.some((m) => m.modelId === modelId) && modelId && (
                      <option value={modelId}>{modelId} (non in catalogo)</option>
                    )}
                    {bedrockModels.map((m) => (
                      <option key={m.modelId} value={m.modelId}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                  {bedrockModelsError && (
                    <p className="muted section-hint">{bedrockModelsError}</p>
                  )}
                </div>
                <div>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => setEnabled(e.target.checked)}
                    />{" "}
                    Abilitato
                  </label>
                </div>
              </div>
            </>
          )}

          {section === "access" && (
            <section className="editor-panel">
              <h2>Accesso</h2>
              <p className="muted section-hint">
                Chi può vedere e invocare questo agente dalle app satellite (oltre al requisito
                crediti). I superadmin bypassano sempre i gate.
              </p>
              <label>
                Visibilità
                <select
                  value={visibility}
                  onChange={(e) => setVisibility(e.target.value as OceanAgentVisibility)}
                >
                  <option value="PRIVATE">Privato — solo il tuo org personale</option>
                  <option value="ORG">Org — membri della stessa organizzazione</option>
                  <option value="PUBLIC_TEMPLATE">
                    Piattaforma — qualsiasi utente autenticato (con livello e app)
                  </option>
                </select>
              </label>
              <label>
                Livello minimo utente
                <select
                  value={String(minAccessLevel)}
                  onChange={(e) => setMinAccessLevel(Number(e.target.value))}
                >
                  <option value="0">Tutti (base)</option>
                  <option value="1">Premium (gruppo vida-premium)</option>
                  <option value="2">Solo superadmin</option>
                </select>
              </label>
              {visibility === "PRIVATE" && minAccessLevel === 0 ? (
                <p className="muted section-hint">
                  Nota: un agente Privato resta invocabile solo dal proprietario, indipendentemente
                  dal livello.
                </p>
              ) : null}
            </section>
          )}

          {section === "assignments" && (
            <section className="editor-panel">
              <h2 className="editor-panel-title">Assegnazioni catalogo</h2>
              <p className="muted section-hint">
                Seleziona skills, memorie e guardrails dal{" "}
                <Link to="/skills">catalogo org</Link>. Per gestire le definizioni usa la sidebar
                principale.
              </p>

              <h3 className="editor-subtitle">App satellite (invocazione)</h3>
              <p className="muted section-hint">
                App che possono invocare questo agente via <code>invokeOceanAgent</code> o streaming
                SSE (header <code>X-Vida-AppId</code>).
              </p>
              <div className="resource-grid resource-grid--apps">
                {OCEAN_ASSIGNABLE_APP_IDS.map((app) => (
                  <label key={app.id} className="resource-card">
                    <input
                      type="checkbox"
                      checked={appIds.includes(app.id)}
                      onChange={() => toggleId(appIds, app.id, setAppIds)}
                    />
                    <span className="resource-card-content">
                      <strong className="resource-card-title">{app.label}</strong>
                      <span className="resource-card-meta muted">{app.id}</span>
                    </span>
                  </label>
                ))}
              </div>

              <h3 className="editor-subtitle">Skills</h3>
              <div className="resource-grid">
                {orgSkills.map((s) => (
                  <label key={s.id} className="resource-card">
                    <input
                      type="checkbox"
                      checked={skillIds.includes(s.id)}
                      onChange={() => toggleId(skillIds, s.id, setSkillIds)}
                    />
                    <span className="resource-card-content">
                      <span className="resource-card-head">
                        <span className="resource-icon">{s.icon}</span>
                        <strong className="resource-card-title">{s.name}</strong>
                      </span>
                      {s.description ? (
                        <span className="resource-card-meta muted">{s.description}</span>
                      ) : null}
                    </span>
                  </label>
                ))}
              </div>

              <h3 className="editor-subtitle">Memorie</h3>
              <div className="resource-grid">
                {orgMemories.map((m) => (
                  <label key={m.id} className="resource-card">
                    <input
                      type="checkbox"
                      checked={memoryIds.includes(m.id)}
                      onChange={() => toggleId(memoryIds, m.id, setMemoryIds)}
                    />
                    <span className="resource-card-content">
                      <span className="resource-card-head">
                        <span className="resource-icon">🧠</span>
                        <strong className="resource-card-title">{m.name}</strong>
                      </span>
                      <span className="resource-card-meta muted">{m.scope}</span>
                    </span>
                  </label>
                ))}
              </div>

              <h3 className="editor-subtitle">Guardrails</h3>
              <div className="resource-grid">
                {orgGuardrails.map((g) => (
                  <label key={g.id} className="resource-card">
                    <input
                      type="checkbox"
                      checked={guardrailIds.includes(g.id)}
                      onChange={() => toggleId(guardrailIds, g.id, setGuardrailIds)}
                    />
                    <span className="resource-card-content">
                      <span className="resource-card-head">
                        <span className="resource-icon">🛡️</span>
                        <strong className="resource-card-title">{g.name}</strong>
                      </span>
                      <span className="resource-card-meta muted">{g.ruleText}</span>
                    </span>
                  </label>
                ))}
              </div>
            </section>
          )}

          {section === "team" && role === "ORCHESTRATOR" && (
            <section className="editor-panel">
              <h2 className="editor-panel-title">Agenti del team</h2>
              <p className="muted section-hint">
                Seleziona gli agenti di cui l&apos;orchestratore deve conoscere identità e/o skills.
              </p>
              {peerCandidates.length === 0 ? (
                <p className="muted">Crea altri agenti prima di configurare l&apos;orchestratore.</p>
              ) : (
                <ul className="peer-list">
                  {peerCandidates.map((a) => {
                    const selected = orchestratorPeers.some((p) => p.agentId === a.id);
                    const peer = peerConfig(a.id);
                    return (
                      <li key={a.id} className="peer-row">
                        <label className="peer-select">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={(e) => setPeerSelected(a.id, e.target.checked)}
                          />
                          <AgentAvatar agent={a} size="sm" />
                          <span>
                            <strong>{a.name}</strong> <span className="chip">{a.role}</span>
                          </span>
                        </label>
                        {selected && (
                          <div className="peer-flags">
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={peer.shareIdentity}
                                onChange={(e) =>
                                  updatePeer(a.id, { shareIdentity: e.target.checked })
                                }
                              />{" "}
                              Identità
                            </label>
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={peer.shareSkills}
                                onChange={(e) => updatePeer(a.id, { shareSkills: e.target.checked })}
                              />{" "}
                              Skills
                            </label>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}

          {section === "budget" && (
            <section className="editor-panel">
              <h2 className="editor-panel-title">Budget e limiti</h2>
              <p className="muted section-hint">
                Limiti di costo e numero massimo di chiamate tool per missione.
              </p>
              <div className="grid-2">
                <div>
                  <label htmlFor="budget">Budget max (centesimi)</label>
                  <input
                    id="budget"
                    type="number"
                    min={1}
                    value={maxCostCents}
                    onChange={(e) => setMaxCostCents(Number(e.target.value))}
                  />
                </div>
                <div>
                  <label htmlFor="toolcalls">Max tool calls</label>
                  <input
                    id="toolcalls"
                    type="number"
                    min={1}
                    value={maxToolCalls}
                    onChange={(e) => setMaxToolCalls(Number(e.target.value))}
                  />
                </div>
              </div>
            </section>
          )}

          <div className="form-actions">
            <button type="submit" disabled={saving}>
              {saving ? "Salvataggio…" : isNew ? "Crea agente" : "Salva"}
            </button>
            {!isNew && agentId && (
              <Link className="btn" to={`/agents/${agentId}/test`}>
                Testa
              </Link>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
